import { apiClient } from './client';
import type {
  EventDressCode,
  CreateDressCodeDto,
  UpdateDressCodeDto,
  BulkCreateDressCodeDto,
} from '@/types/dressCode.types';

export class DressCodeService {

  /**
   * Get all dress codes for an event.
   */
  async getDressCodes(eventId: number, celebrationId: number): Promise<EventDressCode[]> {
    const response = await apiClient.get(
      `/events/${eventId}/dress-codes?celebrationId=${celebrationId}`
    );
    return response.data.data || [];
  }

  /**
   * Create a single dress code entry.
   */
  async createDressCode(eventId: number, dto: CreateDressCodeDto): Promise<EventDressCode> {
    const response = await apiClient.post(`/events/${eventId}/dress-codes`, dto);
    return response.data.data;
  }

  /**
   * Bulk apply a template group to an event.
   */
  async bulkCreate(eventId: number, dto: BulkCreateDressCodeDto): Promise<EventDressCode[]> {
    try {
      const response = await apiClient.post(`/events/${eventId}/dress-codes/bulk`, dto);
      return response.data.data || [];
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Failed to apply template');
      }
      throw error;
    }
  }

  /**
   * Update a dress code entry.
   */
  async updateDressCode(eventId: number, id: number, dto: UpdateDressCodeDto): Promise<EventDressCode> {
    const response = await apiClient.put(`/events/${eventId}/dress-codes/${id}`, dto);
    return response.data.data;
  }

  /**
   * Soft-delete a dress code entry.
   */
  async deleteDressCode(eventId: number, id: number, celebrationId: number): Promise<void> {
    await apiClient.delete(
      `/events/${eventId}/dress-codes/${id}?celebrationId=${celebrationId}`
    );
  }

  /**
   * Toggle the dress code section on/off for an event.
   */
  async toggleDressCode(
    eventId: number,
    celebrationId: number,
    enabled: boolean
  ): Promise<{ enabled: boolean }> {
    const response = await apiClient.patch(`/events/${eventId}/dress-code-toggle`, {
      celebration_id: celebrationId,
      enabled,
    });
    return response.data.data;
  }
}

export const dressCodeService = new DressCodeService();
